import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, PageHeader, Spinner, Badge, StatCard } from "@/components/ui";
import { AlertTriangle, TrendingUp, ShieldCheck } from "lucide-react";
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
  BarChart, Bar,
} from "recharts";

const COLOR = { High: "#dc2626", Rising: "#f59e0b", Low: "#4f46e5" };

export default function RiskStratification() {
  const [members, setMembers] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/members").then((r) => setMembers(r.data)); }, []);
  if (!members) return <Spinner />;

  const counts = { High: 0, Rising: 0, Low: 0 };
  members.forEach((m) => (counts[m.risk_level] = (counts[m.risk_level] || 0) + 1));

  const buckets = [
    { name: "0–24", min: 0, max: 24 }, { name: "25–44", min: 25, max: 44 },
    { name: "45–59", min: 45, max: 59 }, { name: "60–74", min: 60, max: 74 },
    { name: "75–100", min: 75, max: 100 },
  ].map((b) => ({ name: b.name, count: members.filter((m) => m.risk_score >= b.min && m.risk_score <= b.max).length }));

  const scatter = members.map((m) => ({ x: m.risk_score, y: m.care_gaps.length, z: m.conditions.length, level: m.risk_level, name: `${m.first_name} ${m.last_name}`, id: m.id }));
  const highRisk = members.filter((m) => m.risk_level === "High").slice(0, 12);

  return (
    <div className="vc-fade-up">
      <PageHeader title="Risk Stratification" subtitle="Predictive acuity model across the managed population. Identify and prioritize members needing intensive care coordination." />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="High Risk" value={counts.High} sub="Immediate coordination" icon={AlertTriangle} tone="red" />
        <StatCard label="Rising Risk" value={counts.Rising} sub="Preventive outreach" icon={TrendingUp} tone="amber" />
        <StatCard label="Low Risk / Stable" value={counts.Low} sub="Routine monitoring" icon={ShieldCheck} tone="brand" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <Card className="p-6">
          <h3 className="font-display font-semibold text-lg mb-1">Risk Score Distribution</h3>
          <p className="text-xs text-muted-foreground mb-4">Members grouped by composite risk score</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={buckets}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} fill="#4f46e5" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6">
          <h3 className="font-display font-semibold text-lg mb-1">Risk vs. Open Care Gaps</h3>
          <p className="text-xs text-muted-foreground mb-4">Bubble size = comorbidity count. Click a point to open the chart.</p>
          <ResponsiveContainer width="100%" height={280}>
            <ScatterChart>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
              <XAxis type="number" dataKey="x" name="Risk Score" tick={{ fontSize: 11 }} domain={[0, 100]} />
              <YAxis type="number" dataKey="y" name="Care Gaps" tick={{ fontSize: 11 }} />
              <ZAxis type="number" dataKey="z" range={[40, 220]} />
              <Tooltip cursor={{ strokeDasharray: "3 3" }} formatter={(v, n) => [v, n]} labelFormatter={() => ""} />
              <Scatter data={scatter} onClick={(p) => p && navigate(`/members/${p.id}`)}>
                {scatter.map((s, i) => <Cell key={i} fill={COLOR[s.level]} fillOpacity={0.7} />)}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card className="mt-4 overflow-hidden">
        <div className="px-6 py-4 border-b border-border"><h3 className="font-display font-semibold text-lg">High-Risk Members — Priority Panel</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-6 py-3 font-semibold">Member</th><th className="px-4 py-3 font-semibold">Score</th>
              <th className="px-4 py-3 font-semibold">Domains</th><th className="px-4 py-3 font-semibold">Care Gaps</th>
              <th className="px-4 py-3 font-semibold">Care Manager</th></tr></thead>
            <tbody className="divide-y divide-border">
              {highRisk.map((m) => (
                <tr key={m.id} onClick={() => navigate(`/members/${m.id}`)} className="cursor-pointer hover:bg-muted transition-colors">
                  <td className="px-6 py-3 font-medium">{m.first_name} {m.last_name}</td>
                  <td className="px-4 py-3"><span className="font-mono font-bold text-red-700">{m.risk_score}</span></td>
                  <td className="px-4 py-3 text-muted-foreground">{m.domains.join(", ")}</td>
                  <td className="px-4 py-3">{m.care_gaps.length > 0 ? <Badge tone="High">{m.care_gaps.length} open</Badge> : <span className="text-muted-foreground text-xs">None</span>}</td>
                  <td className="px-4 py-3 text-muted-foreground">{m.care_manager_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
